import { useCallback, useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { toast } from "sonner";
import { Users, Send, Loader2, User } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Combined "Followers + Broadcast" page for coaches.
 *
 * Left column (or top on mobile): who follows me + follower count.
 * Right column: broadcast composer — one field + submit.
 * When the coach broadcasts, everyone in the followers list gets a
 * notification within the next 20s poll cycle.
 */
export default function CoachFollowers() {
  const [followers, setFollowers] = useState([]);
  const ctl = useListControls(followers, { name: (f) => f.name || f.student_name, fields: (f) => [f.name, f.student_name, f.email] });
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [broadcasting, setBroadcasting] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/followers");
      setFollowers(data.followers || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load followers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    const body = form.body.trim();
    if (body.length < 3) { toast.error("Message is too short."); return; }
    setBroadcasting(true);
    try {
      const { data } = await api.post("/coach/broadcast", {
        title: form.title.trim() || undefined,
        body,
      });
      toast.success(`Sent to ${data.notified} follower${data.notified === 1 ? "" : "s"}.`);
      setForm({ title: "", body: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="coach-followers-page">
      <header className="fade-up">
        <div className="label-mono mb-2">AUDIENCE</div>
        <h1 className="h-display text-4xl sm:text-5xl tracking-tighter">Followers & broadcasts</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-xl leading-relaxed">
          Send a quick update to everyone who follows you. Great for opening up new slots, sharing a
          special deal, or letting people know about a change of plan.
        </p>
      </header>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {/* Broadcast composer */}
        <section className="card-surface p-5 fade-up" data-testid="coach-broadcast-panel">
          <div className="flex items-center gap-2 mb-3">
            <Send className="w-4 h-4 text-[#CCFF00]" />
            <div className="label-mono">BROADCAST</div>
          </div>
          <form onSubmit={send} className="space-y-3">
            <div>
              <label className="label-mono mb-1 block text-[10px]">TITLE (optional)</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Adding 3pm slots"
                maxLength={140}
                data-testid="coach-broadcast-title"
                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#CCFF00] outline-none"
              />
            </div>
            <div>
              <label className="label-mono mb-1 block text-[10px]">MESSAGE (up to 800 chars)</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Adding 3pm Wednesday slots next week — DM to book. First come first served."
                rows={4}
                maxLength={800}
                data-testid="coach-broadcast-body"
                className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#CCFF00] outline-none resize-y"
              />
              <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-1 text-right">
                {form.body.length}/800
              </div>
            </div>
            <button
              type="submit"
              disabled={broadcasting || form.body.trim().length < 3}
              className="btn-volt px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="coach-broadcast-send"
            >
              {broadcasting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send to {count} follower{count === 1 ? "" : "s"}
            </button>
          </form>
        </section>

        {/* Followers list */}
        <section className="card-surface p-5 fade-up" data-testid="coach-followers-panel">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-[#CCFF00]" />
            <div className="label-mono">FOLLOWERS</div>
            <span className="label-mono text-[10px] px-1.5 py-0.5" style={{ background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 }}>
              {count}
            </span>
          </div>
          {loading ? (
            <div className="text-xs text-[#A3A3A3] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
          ) : count === 0 ? (
            <div className="text-xs text-[#A3A3A3] leading-relaxed" data-testid="coach-followers-empty">
              No followers yet. Anyone you book with is auto-followed — and players can tap
              <span className="text-[#CCFF00]"> Follow </span> on your profile in the coach directory.
            </div>
          ) : (
            <>
            <ListControlsBar ctl={ctl} placeholder="Search followers…" testid="followers" className="mb-2" />
            <ul className="divide-y divide-[#141414]">
              {ctl.list.map((f) => (
                <li key={f.student_id} className="py-2 flex items-center gap-3" data-testid={`coach-follower-${f.student_id}`}>
                  <div className="w-8 h-8 bg-[#141414] flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-[#A3A3A3]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-white truncate">{f.student_name}</div>
                    <div className="text-[10px] text-[#525252] uppercase tracking-wider">
                      {f.source === "auto_booking" ? "auto — booked with you" : "manual"}
                      {f.followed_at && <> · {new Date(f.followed_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
